import React, { useState, useEffect } from 'react'
import { GET_LIST, useClient } from '@peracto/client'
import { Select } from '@peracto/peracto-ui'
import AsyncSelect from 'react-select/async'
import { Field, FieldArray } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash'
import { faTimesCircle } from '@fortawesome/pro-regular-svg-icons/faTimesCircle'
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus'
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle'

import debounce from 'debounce-promise'
import { ConditionsListValue } from './ConditionsListValue'

export const operatorValues = [
    { label: 'Equals', value: '=' },
    { label: 'Greater than', value: '>' },
    { label: 'Less than', value: '<' },
    { label: 'Greater than or equal', value: '>=' },
    { label: 'Less than or equal', value: '<=' },
    { label: 'Not equal to', value: '!=' },
]

const rowOperatorValues = [
    { label: 'And', value: 'and' },
    { label: 'Or', value: 'or' },
]

const DAYS_SINCE_CREATION = {
    label: 'Days Since Creation',
    code: 'daysSinceCreation',
}

const PRICE_TYPE = {
    label: 'Price Type',
    code: 'priceType',
}

const ConditionsListForm = ({ index, form, field, remove, toggleRow }) => {
    const { client } = useClient()

    const fetchAttributes = async (inputValue = '') => {
        const { data } = await client(GET_LIST, 'attributes', {
            id: 'attributes',
            label: inputValue,
        })

        const values = []

        if (DAYS_SINCE_CREATION.label.toLowerCase().includes(inputValue.toLowerCase())) {
            values.push({
                label: `${DAYS_SINCE_CREATION.label} (${DAYS_SINCE_CREATION.code})`,
                value: {
                    label: DAYS_SINCE_CREATION.label,
                    code: DAYS_SINCE_CREATION.code,
                    dataType: 'number',
                    options: [],
                },
            })
        }

        if (PRICE_TYPE.label.toLowerCase().includes(inputValue.toLowerCase())) {
            values.push({
                label: `${PRICE_TYPE.label} (${PRICE_TYPE.code})`,
                value: {
                    label: PRICE_TYPE.label,
                    code: PRICE_TYPE.code,
                    dataType: 'string',
                    options: [],
                },
            })
        }

        const parsedValues = [
            ...values,
            ...data.map(attr => ({
                label: `${attr.label} (${attr.code})`,
                value: {
                    label: attr.label,
                    code: attr.code,
                    dataType: attr.dataType,
                    options: attr.options,
                },
            })),
        ]

        return parsedValues
    }

    const debouncedFetchAttributes = debounce(fetchAttributes, 200)

    const [attributeValues, setAttributeValues] = useState([])

    const [showCustomRule, setShowCustomRule] = useState(
        field.value?.[index]?.attribute?.label === 'Custom Rule'
    )

    useEffect(() => {
        async function fetchAttributeValues() {
            if (form.values.options.attributes[index]?.attribute?.code) {
                const { data } = await client(GET_LIST, 'attributes', {
                    id: 'attributes',
                    filter: {
                        code: form.values.options.attributes[index].attribute.code,
                        itemsPerPage: 1000,
                    },
                })

                if (data?.length > 0) {
                    const group = data.find(
                        d => d.code === form.values.options.attributes[index].attribute.code
                    )

                    if (group && group?.options?.length > 0) {
                        setAttributeValues(group.options)
                    }
                }
            }
        }

        fetchAttributeValues()
    }, [])

    return (
        <div key={`condition_${index}`} className="!border-b !border-b-[#dee2e6] !py-4">
            <div className="row !mb-1" data-testid={`rule-${index}`}>
                <div className="col-12">
                    <div className="form-row">
                        <div className="col-12">
                            <div className="!flex !justify-end !items-center">
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-link !text-danger !px-0 !mr-4"
                                        onClick={() => remove(index)}
                                        data-testid={`remove-condition-${index}`}
                                    >
                                        <FontAwesomeIcon icon={faTrash} /> Remove Condition
                                    </button>

                                    <button
                                        className="btn btn-link !px-0"
                                        onClick={() => toggleRow()}
                                        data-testid={`close-condition-${index}`}
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} className="!mr-2" />
                                        Close Condition
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="col-12 col-md-4">
                            <Field name={`${field.name}.${index}.attribute`}>
                                {({ form, field }) => (
                                    <>
                                        <div className="!flex !justify-between">
                                            <label className="!mb-1">Rule</label>

                                            <button
                                                className="btn btn-link !p-0 text-small"
                                                onClick={e => {
                                                    e.preventDefault()
                                                    if (
                                                        showCustomRule &&
                                                        field.value?.code?.length === 0
                                                    ) {
                                                        form.setFieldValue(
                                                            `${field.name}.code`,
                                                            'Please select...'
                                                        )
                                                        form.setFieldValue(
                                                            `${field.name}.label`,
                                                            ''
                                                        )
                                                    } else {
                                                        if (
                                                            field.value?.code === 'Please select...'
                                                        ) {
                                                            form.setFieldValue(
                                                                `${field.name}.code`,
                                                                ''
                                                            )
                                                        }
                                                    }
                                                    setShowCustomRule(!showCustomRule)
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={showCustomRule ? faTimesCircle : faPlus}
                                                    className="!mr-1"
                                                />
                                                {showCustomRule ? 'Cancel' : 'Add Custom Rule'}
                                            </button>
                                        </div>
                                        {showCustomRule ? (
                                            <div className="form-group">
                                                <input
                                                    className="form-control"
                                                    onChange={e => {
                                                        form.setFieldValue(
                                                            `${field.name}.code`,
                                                            e.target.value
                                                        )
                                                        form.setFieldValue(
                                                            `${field.name}.label`,
                                                            'Custom Rule'
                                                        )
                                                    }}
                                                    value={field.value?.code}
                                                    disabled={form.isSubmitting}
                                                />
                                            </div>
                                        ) : (
                                            <div data-testid={`rule-${index}-dropdown`}>
                                                <AsyncSelect
                                                    className="!w-full"
                                                    classNamePrefix="list"
                                                    loadOptions={input =>
                                                        debouncedFetchAttributes(input)
                                                    }
                                                    isSearchable={true}
                                                    onChange={option => {
                                                        form.setFieldValue(field.name, option.value)
                                                    }}
                                                    value={{
                                                        label: `${field.value.label} (${field.value.code})`,
                                                        value: field.value,
                                                    }}
                                                    placeholder="Search for Attributes to add..."
                                                    noOptionsMessage={({ inputValue }) => {
                                                        if (inputValue.length > 0) {
                                                            return `No results found for '${inputValue}'.`
                                                        } else {
                                                            return 'Enter text to begin searching.'
                                                        }
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </Field>
                        </div>
                        <div className="col-12 col-md-2">
                            <div data-testid={`operator-${index}-dropdown`}>
                                <Select
                                    name={`${field.name}.${index}.operator`}
                                    placeholder="Select..."
                                    options={rowOperatorValues}
                                    label="Operator"
                                    disabled={
                                        form.values?.options?.attributes[index]?.values?.length < 2
                                    }
                                />
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <label className="!mb-1">Values</label>
                            <Field name={`${field.name}.${index}.values`}>
                                {({ form, field }) => (
                                    <FieldArray name={field.name}>
                                        {({ push, remove }) => {
                                            return (
                                                <>
                                                    {field.value &&
                                                        field.value.length > 0 &&
                                                        field.value.map((option, valIdx) => (
                                                            <ConditionsListValue
                                                                key={`${field.name}.${valIdx}`}
                                                                field={field}
                                                                form={form}
                                                                valIdx={valIdx}
                                                                index={index}
                                                                remove={remove}
                                                                attributeValues={attributeValues}
                                                            />
                                                        ))}

                                                    {!field.value ||
                                                        (field.value.length === 0 && (
                                                            <p className="!py-4 !my-4 !text-center">
                                                                No values applied
                                                            </p>
                                                        ))}

                                                    <div className="!flex !justify-end !items-end">
                                                        <button
                                                            className="btn btn-link !px-0"
                                                            onClick={e => {
                                                                e.preventDefault()
                                                                push({
                                                                    operation: '=',
                                                                    value: '',
                                                                })
                                                            }}
                                                            data-testid={`add-value-rule-${index}`}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faPlusCircle}
                                                                className="!mr-2"
                                                            />
                                                            Add Value
                                                        </button>
                                                    </div>
                                                </>
                                            )
                                        }}
                                    </FieldArray>
                                )}
                            </Field>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ConditionsListForm
